<template>
  <div>
    <v-col
      cols="9"
      v-if="loading"
      class="mx-auto loading-progress"
      justify-self="center"
    >
      <v-progress-linear
        rounded
        height="6"
        indeterminate
        color="orange accent-4"
      ></v-progress-linear>
      <p class="text-center my-5">Loading..</p>
    </v-col>

    <div v-else>
      <div
        v-if="manuscript.folder"
        :class="$vuetify.breakpoint.mdAndUp ? 'd-flex' : ''"
      >
        <h2>
          Manuscript Details &mdash;
          {{ formatFolderId(manuscript.folder._id) }}
        </h2>

        <v-spacer class="mb-5"></v-spacer>

        <v-btn
          class="white--text"
          color="blue accent-4"
          @click="showCommunications = !showCommunications"
        >
          {{ showCommunications ? "Hide" : "Show" }} Communications
          <v-icon right>mdi-forum-outline</v-icon>
        </v-btn>
      </div>

      <v-scale-transition>
        <ManuscriptCommunications
          class="mt-7"
          v-if="showCommunications"
          :manuscript="manuscript._id"
        />
        <v-row class="mt-5" v-else>
          <v-col cols="12" md="7">
            <v-alert
              border="top"
              type="error"
              class="mb-5 pt-7 pb-5"
              elevation="2"
              prominent
              color="red"
              v-if="showOutdatedWarning"
            >
              <div
                :class="$vuetify.breakpoint.mdAndUp ? 'd-flex' : ''"
                class="ma-0"
              >
                This is an older version of this manuscript
                <v-spacer></v-spacer>
                <v-btn
                  small
                  light
                  depressed
                  v-if="manuscript.newestVersion"
                  color="grey--text text--darken-3"
                  :to="`./${manuscript.newestVersion}`"
                >
                  See newest version
                </v-btn>
              </div>
            </v-alert>

            <v-card>
              <div class="pa-4">
                <div
                  :class="$vuetify.breakpoint.mdAndUp ? 'd-flex' : ''"
                  class="mb-4"
                >
                  <v-chip outlined small class="mb-1 mr-2">
                    {{ manuscript.type }}
                  </v-chip>

                  <v-spacer></v-spacer>

                  <v-chip class="mb-1 mr-2" outlined small>
                    Status: {{ manuscriptStatus }}
                  </v-chip>

                  <v-chip
                    small
                    class="mb-1 mr-2"
                    v-if="manuscript.decision"
                    :color="decisionChip.color"
                  >
                    Decision: {{ decisionChip.decision }}&nbsp;
                    <v-icon>{{ decisionChip.icon }}</v-icon>
                  </v-chip>
                </div>
                <h2
                  style="line-height: 1.2"
                  class="paper-title green--text text--darken-3"
                >
                  {{ manuscript.title }}
                </h2>
              </div>
              <v-divider></v-divider>
              <v-card-text>
                <h4>Abstract</h4>
                <div class="abstract">
                  <div id="abstract" v-html="manuscript.abstract"></div>
                </div>
                <p>
                  <strong class="label mr-2">Keywords:</strong>
                  <v-chip
                    small
                    :key="word"
                    color="green lighten-4"
                    class="mr-1 my-1"
                    v-for="word in manuscript.keywords"
                  >
                    {{ word }}
                  </v-chip>
                </p>
              </v-card-text>
              <v-card-actions
                class="
                  lime
                  lighten-4
                  text-center
                  justify-center
                  green--text
                  text--darken-3
                "
                v-if="manuscript.special"
              >
                <h5 class="my-1 text-uppercase text--center">
                  {{ manuscript.special.title }}
                </h5>
              </v-card-actions>
            </v-card>

            <v-card class="my-6">
              <v-card-title> Authors </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <ol>
                  <li
                    v-for="(author, index) in manuscript.authors"
                    :key="index"
                  >
                    <AuthorDetails :author="author" />
                  </li>
                </ol>
              </v-card-text>
            </v-card>

            <v-card class="my-6">
              <v-card-title> Uploaded Files </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <ol class="my-4" v-if="manuscript.files.length > 0">
                  <li v-for="(file, index) in manuscript.files" :key="index">
                    <h3 class="d-flex">
                      {{ file.name }}
                      <v-spacer></v-spacer>
                      <v-btn
                        x-small
                        outlined
                        color="teal"
                        class="mr-1 mb-1"
                        :href="file.location"
                        :download="manuscript.title"
                      >
                        Download
                      </v-btn>
                    </h3>
                    <address>
                      <p v-if="file.description">{{ file.description }}</p>
                      <p>{{ formatDate(file.createdAt) }}</p>
                    </address>
                  </li>
                </ol>
              </v-card-text>
            </v-card>

            <h2 class="text-center">Manuscript Reviews</h2>

            <v-card class="my-6">
              <v-card-text>
                <div
                  class="pa-5 text-center red--text"
                  v-if="reviews.length == 0"
                >
                  <h4>There are no reviews for this manuscript yet</h4>
                </div>
                <ManuscriptReview
                  v-else
                  v-for="(review, index) in reviews"
                  :review="review"
                  :key="review._id"
                  :class="index != 0 ? 'mt-3' : ''"
                />
              </v-card-text>
            </v-card>

            <ManuscriptVersions class="mt-8" :versions="versions" :manuscript="manuscript" />
          </v-col>

          <v-col cols="12" md="5">
            <div
              v-if="
                manuscriptStatus !== 'INCOMPLETE' && manuscript.currentVersion
              "
            >
              <v-card class="mb-5">
                <v-card-title>Manuscript Decision</v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <P class="mb-6 red--text">
                    This cannot be undone. Only make decision when it has been
                    finalized.
                  </P>

                  <v-form
                    class="mr-2 my-3"
                    @submit.prevent="updateManuscriptDecision"
                  >
                    <v-select
                      outlined
                      clearable
                      label="Manuscript decision *"
                      :items="manuscriptDecisions"
                      prepend-inner-icon="mdi-gavel"
                      v-model="manuscript.decision"
                    ></v-select>

                    <v-btn
                      type="submit"
                      class="white--text"
                      color="pink darken-3"
                      :loading="updatingDecision"
                      :disabled="
                        updatingDecision ||
                        !manuscript.decision ||
                        manuscript.manuscriptStatus == statuses.new ||
                        manuscript.decision == manuscriptDecision
                      "
                    >
                      Make Decision
                    </v-btn>
                  </v-form>
                </v-card-text>
              </v-card>
              
              <v-card class="mb-5">
                <v-card-title>Manuscript Status</v-card-title>
                <v-divider></v-divider>
                <!-- :disabled="!!decision" -->
                <v-card-text>
                  <v-form
                    class="mr-2 my-3"
                    v-model="statusFormValid"
                    @submit.prevent="updateManuscriptStatus"
                  >
                    <v-select
                      outlined
                      :rules="required"
                      label="Manuscript status *"
                      :items="availableStatuses"
                      prepend-inner-icon="mdi-sticker-plus-outline"
                      v-model="manuscript.manuscriptStatus"
                    ></v-select>

                    <v-btn
                      type="submit"
                      class="white--text"
                      color="orange accent-4"
                      :loading="updatingStatus"
                      :disabled="updatingStatus || !statusFormValid"
                    >
                      Update Status
                    </v-btn>
                  </v-form>
                </v-card-text>
              </v-card>

              <v-card class="mt-5" color="lime lighten-4" v-if="canAssign">
                <v-card-title>Assign Submission to Editor</v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <v-form
                    class="mr-2 my-3"
                    @submit.prevent="assignFolderToEditor"
                  >
                    <v-autocomplete
                      outlined
                      clearable
                      item-value="_id"
                      :items="availableEditors"
                      :filter="journalReviewersFilter"
                      label="Search for editor"
                      v-model="selectedEditor"
                      prepend-inner-icon="mdi-account-supervisor-circle-outline"
                    >
                      <template v-slot:selection="data">
                        {{ data.item.title }} {{ data.item.firstName }}
                        {{ data.item.lastName }}
                        <small class="ml-1" v-if="data.item.specialization">
                          ({{ data.item.specialization }})
                        </small>
                      </template>
                      <template v-slot:item="data">
                        <template v-if="typeof data.item !== 'object'">
                          <v-list-item-content
                            v-text="data.item"
                          ></v-list-item-content>
                        </template>
                        <template v-else>
                          <v-list-item-content>
                            <v-list-item-title>
                              {{ data.item.title }}
                              {{ data.item.firstName }}
                              {{ data.item.lastName }}
                              <small
                                class="ml-1"
                                v-if="data.item.specialization"
                              >
                                ({{ data.item.specialization }})
                              </small>
                            </v-list-item-title>
                            <v-list-item-subtitle>
                              {{ data.item.institution }} &dash;
                              {{ data.item.department }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </template>
                    </v-autocomplete>

                    <v-btn
                      type="submit"
                      class="white--text"
                      color="teal darken-3"
                      :loading="assigningEditor"
                      :disabled="
                        assigningEditor ||
                        !selectedEditor ||
                        selectedEditor == currentEditor
                      "
                    >
                      Assign to Editor
                    </v-btn>
                  </v-form>
                </v-card-text>
              </v-card>
            </div>

            <ManuscriptReviewerInvitation
              v-if="!decision"
              :pending="internalReviewersInvited"
              :manuscript="manuscript"
              :invites="
                manuscriptStatus !== 'INCOMPLETE' && manuscript.currentVersion
              "
              @refresh="fetchManuscript"
            />
          </v-col>
        </v-row>
      </v-scale-transition>
    </div>
  </div>
</template>

<script>
import { formatDate, formatFolderId } from "../../tools/format";
import { required } from "../../tools/rules";
import { decisionChip } from "../../tools/decision-chip";

import {
  manuscriptStatus,
  manuscriptStatuses,
  manuscriptStatusesHavingDecision,
  manuscriptDecisions,
} from "../../tools/manuscript";

import AuthorDetails from "../Author/Includes/AuthorDetails";
import ManuscriptReview from "../Review/Includes/ManuscriptReview";
import ManuscriptCommunications from "../Communication/Includes/ManuscriptCommunications";

import ManuscriptVersions from "./Includes/ManuscriptVersions.vue";
import ManuscriptReviewerInvitation from "./Includes/ManuscriptReviewerInvitation.vue";

export default {
  props: ["manuscriptId"],

  components: {
    AuthorDetails,
    ManuscriptReview,
    ManuscriptCommunications,
    ManuscriptVersions,
    ManuscriptReviewerInvitation,
  },

  data: () => ({
    required,
    manuscript: {
      abstract: "",
      files: [],
      invitations: [],
      folder: null,
      decision: null,
    },
    reviews: [],
    internalReviewersInvited: [],

    statusFormValid: true,
    statuses: manuscriptStatus,

    manuscriptStatus: "INCOMPLETE",

    manuscriptDecision: null,
    manuscriptDecisions,

    updatingStatus: false,
    updatingDecision: false,
    assigningEditor: false,

    availableEditors: [],
    selectedEditor: null,
    currentEditor: null,

    decision: null,
    showCommunications: false,
    versions: [],
    loading: false,
  }),

  computed: {
    availableStatuses() {
      if (this.manuscript.decision) {
        return manuscriptStatusesHavingDecision;
      } else {
        return manuscriptStatuses;
      }
    },

    decisionChip() {
      return decisionChip(this.manuscript.decision);
    },

    showOutdatedWarning() {
      return !this.manuscript.currentVersion;
    },

    canAssign() {
      const user = JSON.parse(localStorage.getItem("jmesrUser"));
      return user.roles.includes("ADMIN") || user.roles.includes("PUBLISHER");
    },
  },

  methods: {
    formatDate,
    formatFolderId,
    // Todo: put this in a shared place so that it is available app-wide?
    journalReviewersFilter(item, queryText) {
      const email = item.email.toLowerCase();
      const specialization = item.specialization
        ? item.specialization.toLowerCase()
        : "";
      const institution = item.institution.toLowerCase();
      const department = item.department.toLowerCase();
      const firstName = item.firstName.toLowerCase();
      const lastName = item.lastName.toLowerCase();
      const searchText = queryText.toLowerCase();

      return (
        email.indexOf(searchText) > -1 ||
        specialization.indexOf(searchText) > -1 ||
        institution.indexOf(searchText) > -1 ||
        department.indexOf(searchText) > -1 ||
        firstName.indexOf(searchText) > -1 ||
        lastName.indexOf(searchText) > -1
      );
    },
    fetchManuscript() {
      if (this.manuscriptId && !this.loading) {
        this.loading = true;

        this.$axios
          .get(`/manuscript/${this.manuscriptId}`)
          .then(({ data: response }) => {
            this.$bus.$emit("snackbar", {
              type: "info",
              message: "Manuscript loaded",
            });

            this.manuscript = response;
            this.reviews = response.reviews;
            this.decision = response.decision;
            response.manuscriptStatus &&
              (this.manuscriptStatus = response.manuscriptStatus);
            response.decision && (this.manuscriptDecision = response.decision);

            if (response.folder.editor) {
              this.selectedEditor = response.folder.editor._id;
              this.currentEditor = response.folder.editor._id;
            }

            this.internalReviewersInvited = [...response.invitations]
              .filter((inv) => inv.status == "INVITED")
              .map((inv) => inv.reviewer._id);

            this.fetchEditors();
            this.fetchManuscriptVersions(response.folder);
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not load manuscript",
            });
            this.$router.push({
              path: "/editorial/complete",
            });
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    fetchManuscriptVersions({ _id: folder }) {
      if (folder) {
        this.$axios
          .get(`/manuscripts/versions/${folder}`)
          .then(({ data: response }) => {
            this.versions = response;
          });
      }
    },
    fetchEditors() {
      if (this.manuscriptId) {
        this.$axios.get("/editorial/editors").then(({ data: response }) => {
          this.availableEditors = response;
        });
      }
    },
    updateManuscriptStatus() {
      if (!this.updatingStatus) {
        this.updatingStatus = true;

        const data = {
          manuscript: this.manuscript._id,
          status: this.manuscript.manuscriptStatus,
        };

        this.$axios
          .post("/editorial/status", data)
          .then(() => {
            this.$bus.$emit("snackbar", {
              type: "success",
              message: "Manuscript status updated",
            });
            this.fetchManuscript();
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not update status",
            });
          })
          .finally(() => {
            this.updatingStatus = false;
          });
      }
    },
    updateManuscriptDecision() {
      if (!this.updatingDecision) {
        this.updatingDecision = true;

        const data = {
          manuscript: this.manuscript._id,
          decision: this.manuscript.decision,
        };

        this.$axios
          .post("/editorial/decision", data)
          .then(() => {
            this.$bus.$emit("snackbar", {
              type: "success",
              message: "Manuscript decision updated",
            });
            this.fetchManuscript();
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not update decision",
            });
          })
          .finally(() => {
            this.updatingDecision = false;
          });
      }
    },
    assignFolderToEditor() {
      if (!this.assigningEditor) {
        this.assigningEditor = true;

        const data = {
          folder: this.manuscript.folder,
          editor: this.selectedEditor,
        };

        this.$axios
          .post("/editorial/editor", data)
          .then(() => {
            this.$bus.$emit("snackbar", {
              type: "success",
              message: "Manuscript has been assigned",
            });
            this.fetchManuscript();
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not assign manuscript to editor",
            });
          })
          .finally(() => {
            this.assigningEditor = false;
          });
      }
    },

    copyAbstract() {
      var input = document.createElement("textarea");
      input.value = document.getElementById("abstract").textContent();
      document.body.appendChild(input);
      input.select();
      document.execCommand("Copy");
      input.remove();

      this.$bus.$emit("snackbar", {
        type: "info",
        message: "Abstract copied to clipboard",
      });
    },
  },
  mounted() {
    this.fetchManuscript();
    this.$bus.$on("review.invitation.cancelled", this.fetchManuscript);
    this.$bus.$on("review.invitation.reminder", this.fetchManuscript);
  },
};
</script>

<style lang="scss" scoped>
.loading-progress {
  margin-top: 35vh;
}
</style>

<style lang="scss">
.paper-title {
  word-break: keep-all;
}

.abstract {
  margin: 0.25em 0 1em;
  padding: 0.75em 0.25em;
  white-space: pre-line;
  border-top: 1px dashed #dddddd;
  border-bottom: 1px dashed #dddddd;
  position: relative;

  p {
    margin-bottom: 0.5em;
  }

  .copy-abstract {
    top: 0;
    right: 0;
    position: absolute;

    cursor: pointer;
    padding: 0.15em 0.5em;

    opacity: 0;

    // color: #ffffff;
    background: #83c1f3;

    transition: all 300ms ease;
  }

  &:hover {
    .copy-abstract {
      opacity: 1;
    }
  }
}
</style>
